import { pageLanguage, type Language } from '@bn-games/i18n-core';
import type { ThemeName } from '@bn-games/theming';
import type { VariantId } from '../game/variants/index.js';
import type { PlayerCount } from '../game/variants/types.js';

export type { Language, ThemeName };

/** Variants registered in `src/game/variants/index.ts`. */
export type Variant = VariantId;

export type Difficulty = 'easy' | 'medium' | 'hard';

/** Match-end score targets surfaced on the Settings screen. */
export type PointGoal = 100 | 150 | 250;

export interface Settings {
  readonly sound: boolean;
  readonly theme: ThemeName;
  readonly language: Language;
  readonly playerName: string;
  readonly variant: Variant;
  readonly playerCount: PlayerCount;
  readonly difficulty: Difficulty;
  readonly pointGoal: PointGoal;
  /** Strict Muggins steals; lenient auto-scores on play. Mirrors the rule-set flag. */
  readonly mugginsStrict: boolean;
  /**
   * When true, the local player's Muggins points are auto-claimed at the start
   * of the window so they don't have to mash a button. Has no effect in lenient
   * mode (auto-scoring already happens engine-side).
   */
  readonly autoClaimMuggins: boolean;
  /**
   * Hot-seat hide curtain: when true, a "Pass the device" curtain hides each
   * player's hand between turns in local multi-player. Off for solo testing.
   */
  readonly hotSeatHide: boolean;
  readonly afkAssistEnabled: boolean;
  readonly afkTimeoutMs: number;
  /** Controls whether incoming AFK pings play a reminder sound on my side. */
  readonly afkRemindersEnabled: boolean;
  /** Controls whether the "ping opponent" button appears on my side when the opponent is idle. */
  readonly showAfkPingButton: boolean;
  /**
   * When true, online matches the host creates run with drand-derived
   * verifiable shuffles (Phase B of the provably-fair roadmap). Advisory for
   * the local UI only — the room's actual mode is set at CREATE_ROOM and
   * echoed by the server. Off by default; ignored for AI + hot-seat (no
   * opponent to convince).
   */
  readonly verifiableDiceEnabled: boolean;
  /**
   * When true, the boneyard tile-stack on the table renders a numeric count
   * badge on its top tile. Off by default. The dashed "your turn to draw"
   * indicator around the pile is unaffected by this setting — it always
   * surfaces when drawing is the legal action.
   */
  readonly showBoneyardCount: boolean;
  /** Plays a short cue when the turn comes back to me (vs-AI + online only). */
  readonly turnStartSound: boolean;
  /** Козёл only: a side still on 0 only has a hand's penalty written if it is ≥ 13. */
  readonly goatOpening13: boolean;
  /** Козёл only: going out on [0|0] («Лысый») ends the match at once. */
  readonly goatBaldFinish: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  sound: true,
  theme: 'mahogany',
  // A /…/ru/ page opens in Russian unless the player already chose a language.
  language: pageLanguage(),
  playerName: 'Player',
  variant: 'goat',
  playerCount: 2,
  difficulty: 'medium',
  pointGoal: 150,
  mugginsStrict: true,
  autoClaimMuggins: true,
  hotSeatHide: true,
  afkAssistEnabled: true,
  afkTimeoutMs: 10_000,
  afkRemindersEnabled: true,
  showAfkPingButton: true,
  verifiableDiceEnabled: false,
  showBoneyardCount: false,
  turnStartSound: true,
  goatOpening13: false,
  goatBaldFinish: false,
};
